import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/lib/translations";
import { openCookieSettings } from "@/components/site/ConsentBanner";
import { subscribeNewsletter } from "@/lib/api";

export const Footer = () => {
  const { lang, setLang, t } = useLang();
  const docs = t.legalUI.docs;
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      await subscribeNewsletter(email);
      setSubscribed(true);
      toast.success("You're in — fortnightly field updates incoming.");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
  };

  return (
    <footer className="relative z-10 bg-[#0A0A0C] border-t border-white/10 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12">
          <div>
            <h2 className="font-future text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-none">
              PivoT<span className="font-origin italic font-light text-white/60"> ransition</span>
            </h2>
            <p className="mt-6 text-white/50 max-w-md leading-relaxed">
              PivoT ransition — a career transition guide &amp; workbook by Pivot Transfer by
              Raphael. Cross into Fintech, AI, Robotics and Machine Learning, deliberately.
            </p>

            <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-white/15 p-1" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  aria-pressed={lang === l.code}
                  className={`label-caps px-3 py-1.5 rounded-full transition-colors duration-300 ${
                    lang === l.code ? "bg-white text-black" : "text-white/60 hover:text-white"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <nav className="flex flex-col gap-4 label-caps text-white/60">
            <Link data-testid="footer-terms" to="/terms" className="hover:text-white transition-colors duration-300">{docs.terms}</Link>
            <Link data-testid="footer-privacy" to="/privacy" className="hover:text-white transition-colors duration-300">{docs.privacy}</Link>
            <Link data-testid="footer-use" to="/terms-of-use" className="hover:text-white transition-colors duration-300">{docs.use}</Link>
            <Link data-testid="footer-support" to="/support" className="hover:text-white transition-colors duration-300">Support &amp; chat</Link>
            <button
              data-testid="footer-cookie-settings"
              onClick={openCookieSettings}
              className="text-left hover:text-white transition-colors duration-300"
            >
              {t.legalUI.cookieSettings}
            </button>
          </nav>
        </div>

        {/* Newsletter */}
        <div className="mt-16 pt-10 border-t border-white/10 flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-testid="newsletter">
          <div>
            <h3 className="font-future text-lg text-white">Field updates, every fortnight</h3>
            <p className="text-white/50 text-sm mt-1 max-w-md">Join the crossing — get bi-weekly developments in AI, Fintech & Robotics, plus new releases.</p>
          </div>
          {subscribed ? (
            <div className="flex items-center gap-2 text-[var(--neon-cyan)]" data-testid="newsletter-success">
              <Check size={18} /> <span className="font-future">Subscribed — see you in two weeks.</span>
            </div>
          ) : (
            <form onSubmit={subscribe} className="flex gap-2 w-full lg:w-auto">
              <Input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-white/5 border-white/20 text-white placeholder:text-white/30 h-12 lg:w-72"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="label-caps h-12 px-6 rounded-lg bg-white text-black hover:bg-[var(--neon-cyan)] transition-colors duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                Subscribe <Send size={15} />
              </button>
            </form>
          )}
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 font-mono2 text-xs text-white/35">
          <span>© {new Date().getFullYear()} Pivot Transfer by Raphael · Zürich, Switzerland</span>
          <span className="flex items-center gap-4">
            <span>Secure payments by Stripe · Prices in SFr</span>
            <Link data-testid="footer-admin" to="/admin" className="hover:text-white transition-colors duration-300">Admin</Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
